import { defineStore } from "pinia";
import { SessionDetailsApi } from "@simbelapp/order-sdk";
import { useSessionDetailsStore } from "./session-details-store";
import type { ISessionsDetailsFinancing } from "~/utils/interfaces/sessions-interfaces";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { MicroserviceEnum, VatEnum } from "~/utils/enums/common.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { SessionItemTypeEnum } from "~/utils/enums/session-items.enums";

export const useSessionDetailsFinancingStore = defineStore("sessionDetailsFinancingStore", {
  state: (): ISessionsDetailsFinancing => {
    return {
      financing: {
        isCPFEligible: false,
        isOpcoEligible: false,
        isWithSession: false,
        isWithTraining: false,
        priceExclTax: null,
        totalAttendees: null,
        opcoAmountExclTax: null,
        budgetAmountExclTax: null,
        cpfAmountExclTax: null,
        groupPriceExclTax: null,
        individualPriceExclTax: null,
        maxNbAttendees: null,
        isIndividualSession: false,
        vatType: null,
        usersBuckets: null,
        trainingIsDraft: false,
        trainingIsVisible: false,
      },
    };
  },
  actions: {
    async getFinancing() {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails, sessionTraining } = storeToRefs(sessionDetailsStore);
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
      if (!sessionDetails.value?.id) {
        return null;
      }
      try {
        const data = await apiInstance.getAttendeesWithPrices({
          itemId: sessionDetails.value?.id,
          itemType: sessionDetails.value?.itemType,
        });
        this.financing = {
          // `isCpfEligible` / `isOpcoEligible` / the session-item `vat` aren't part of the SDK
          // `Catalog` contract, so these three can't be derived from `sessionTraining`.
          isCPFEligible: false,
          isOpcoEligible: false,
          isWithSession: true,
          isWithTraining: true,
          priceExclTax: data?.totalByBucket?.priceExclTax ?? null,
          totalAttendees: data.attendees?.length ?? 0,
          // Per-mode amounts now live on `financingModes[]` (read directly by the recap);
          // these legacy 3-mode aggregates are no longer surfaced.
          opcoAmountExclTax: null,
          budgetAmountExclTax: null,
          cpfAmountExclTax: null,
          groupPriceExclTax: sessionTraining.value?.groupPriceExclTax ?? null,
          individualPriceExclTax: sessionTraining.value?.individualPriceExclTax ?? null,
          maxNbAttendees: sessionTraining.value?.groupSessionItem?.maxNbAttendees ?? null,
          isIndividualSession:
            (!!sessionTraining.value?.individualSessionItem &&
              sessionDetails.value?.sessionType === SessionItemTypeEnum.INDIVIDUAL) ??
            false,
          vatType: VatEnum.HT,
          usersBuckets: data.attendees,
          trainingIsDraft: sessionDetailsStore.isTrainingStatusDraft ?? false,
          trainingIsVisible: true,
        };
      } catch (error) {
        console.error("Erreur lors de la récupération des détails de la session:", error);
        feedback.error(`Une erreur est survenue lors de la récupération des détails`, "small");
        throw error;
      }
    },
  },
});
